'use client';

import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCookieValue, IS_SSR } from '@libs/common-next';
import { stringifyStoreId } from '@libs/core';

import { STORE_COOKIE_NAME } from './constants';
import { StoreContext, StoreDispatchContext } from './context';
import { useStoreQuery } from './queries';
import type { StoreProviderDispatch, StoreProviderState } from './types';
import { removeStoreCookie, setStoreCookie } from './utils';

/**
 * In the previous implementation there was no difference between selected and favorite stores
 * That's why we are not supporting favorite store any more.
 *
 * In the future, we will extend this provider with favorite and any other types of Stores that are added
 */
export function StoreProvider({ children }: Readonly<{ children: ReactNode }>) {
    const [selectedStoreId, setSelectedStoreId] = useState(() =>
        IS_SSR ? undefined : getCookieValue(STORE_COOKIE_NAME)
    );

    const selectedStore = useStoreQuery(selectedStoreId);

    const updateSelectedStore = useCallback((storeId: string | number) => {
        const parsedStoreId = typeof storeId === 'number' ? (stringifyStoreId(storeId) as string) : storeId;

        setStoreCookie(parsedStoreId);
        setSelectedStoreId(parsedStoreId);
    }, []);

    const clearSelectedStore = useCallback(() => {
        removeStoreCookie();
        setSelectedStoreId(undefined);
    }, []);

    const isPermanentlyClosed = selectedStore.data?.IsActive === false;

    useEffect(() => {
        if (isPermanentlyClosed) {
            clearSelectedStore();
        }
    }, [clearSelectedStore, isPermanentlyClosed]);

    const stateValue = useMemo<StoreProviderState>(
        () => ({
            selectedStore: isPermanentlyClosed
                ? ({ ...selectedStore, data: null } as StoreProviderState['selectedStore'])
                : selectedStore,
            selectedStoreId: isPermanentlyClosed ? undefined : selectedStoreId,
        }),
        [isPermanentlyClosed, selectedStore, selectedStoreId]
    );

    const dispatchValue = useMemo<StoreProviderDispatch>(
        () => ({
            updateSelectedStore,
            clearSelectedStore,
        }),
        [updateSelectedStore, clearSelectedStore]
    );

    return (
        <StoreContext.Provider value={stateValue}>
            <StoreDispatchContext.Provider value={dispatchValue}>{children}</StoreDispatchContext.Provider>
        </StoreContext.Provider>
    );
}
